import { shade } from '../pixel.js';
import type { PixelCanvas } from '../pixel.js';
import { box, drawBox, drawBoxes, drawCylinder, faces, frontFace, iso, seatWithBack } from './iso.js';
import type { FurniDrawer } from './iso.js';

/** Drawers for the rare furniture sold for Gold and Diamonds. */

const GOLD = '#f0bf36';
const GOLD_DARK = '#b98a1e';

function drawGoldenCat(pc: PixelCanvas, rotation: number): void {
  drawBox(pc, box(-0.34, 0.34, -0.3, 0.3, 0, 8, faces('#7a1f1a', '#a52a22')));
  drawBox(pc, box(-0.28, 0.28, -0.24, 0.24, 8, 30, faces(GOLD_DARK, GOLD)));
  drawBox(pc, box(-0.24, 0.24, -0.2, 0.2, 30, 46, faces(GOLD_DARK, GOLD)));
  // Ears, face and the waving paw.
  const head = iso(0, 0, 46);
  pc.rect(head.x - 6, head.y - 5, 3, 4, GOLD);
  pc.rect(head.x + 4, head.y - 5, 3, 4, GOLD);
  const face = iso(0.05, 0.2, 38);
  pc.px(face.x - 3, face.y, '#2a1e19');
  pc.px(face.x + 3, face.y, '#2a1e19');
  pc.rect(face.x - 1, face.y + 3, 3, 1, '#d9433b');
  drawBox(pc, box(0.2, 0.32, -0.08, 0.06, 26, 44, faces(GOLD_DARK, '#ffe27a')));
  const coin = iso(-0.1, 0.16, 20);
  pc.ellipse(coin.x, coin.y, 4, 5, '#ffe27a');
  // Wave the paw the other way when the cat turns to face right.
  if (frontFace(rotation) === 'right') pc.flipX();
}

/**
 * A throne: stepped plinth, cushioned seat, two armrests, a tall back with
 * finials on its top corners and a gem. Armrests flank the sitter, so they
 * swap axes with the rotation.
 */
interface ThronePalette {
  frame: [string, string];
  plinth: [string, string];
  cushion: [string, string];
  gem: string;
}

function throneDrawer(p: ThronePalette, gemShape: 'round' | 'diamond'): FurniDrawer {
  return (pc, rotation) => {
    const alongU = rotation === 0 || rotation === 4;
    const arm = (side: -1 | 1) => {
      const lo = side < 0 ? -0.4 : 0.3;
      const hi = side < 0 ? -0.3 : 0.4;
      return alongU
        ? box(lo, hi, -0.34, 0.34, 10, 34, faces(...p.frame))
        : box(-0.34, 0.34, lo, hi, 10, 34, faces(...p.frame));
    };
    // Backs on the near side (rotations 0 and 6) are drawn low, like a chair's,
    // because the sprite is painted over the sitter; far-side backs stay tall.
    const nearBack = rotation === 0 || rotation === 6;
    const backH = nearBack ? 50 : 78;
    // One depth-sorted pass, so the cushion sits in front of a far back and
    // behind a near one.
    drawBoxes(pc, [
      box(-0.44, 0.44, -0.44, 0.44, 0, 5, faces(...p.plinth)),
      box(-0.4, 0.4, -0.4, 0.4, 5, 10, faces(...p.plinth)),
      arm(-1),
      arm(1),
      ...seatWithBack(rotation, 0.4, [10, 20], backH, 0.14, faces(...p.frame), faces(...p.frame)),
      box(-0.26, 0.26, -0.26, 0.26, 20, 25, faces(...p.cushion)),
    ]);
    const f = 0.34;
    const corners: [number, number][] =
      rotation === 2 ? [[-f, -f], [-f, f]] : rotation === 4 ? [[-f, -f], [f, -f]] : rotation === 6 ? [[f, -f], [f, f]] : [[-f, f], [f, f]];
    for (const [u, v] of corners) drawBox(pc, box(u - 0.06, u + 0.06, v - 0.06, v + 0.06, backH, backH + 10, faces(...p.frame)));
    const gemH = backH - 18;
    const top = rotation === 2 ? iso(-0.3, 0, gemH) : rotation === 4 ? iso(0, -0.3, gemH) : rotation === 6 ? iso(0.3, 0, gemH) : iso(0, 0.3, gemH);
    if (gemShape === 'round') pc.ellipse(top.x, top.y, 3, 3, p.gem);
    else
      pc.poly(
        [
          { x: top.x, y: top.y - 5 },
          { x: top.x + 4, y: top.y },
          { x: top.x, y: top.y + 5 },
          { x: top.x - 4, y: top.y },
        ],
        p.gem,
      );
  };
}

const drawGoldThrone = throneDrawer(
  { frame: [GOLD_DARK, GOLD], plinth: ['#7a1f1a', '#a52a22'], cushion: ['#a52a22', '#d9433b'], gem: '#5bc8f0' },
  'round',
);

function drawDiamondFountain(pc: PixelCanvas): void {
  drawCylinder(pc, 0.46, 0, 10, '#7fb7d9', '#a9b0b7');
  drawCylinder(pc, 0.36, 10, 14, '#bfe8ff', '#7fb7d9');
  drawCylinder(pc, 0.08, 14, 40, '#e6f7ff', '#a9b0b7');
  drawCylinder(pc, 0.22, 40, 44, '#bfe8ff', '#7fb7d9');
  const top = iso(0, 0, 44);
  pc.poly(
    [
      { x: top.x, y: top.y - 16 },
      { x: top.x + 7, y: top.y - 7 },
      { x: top.x, y: top.y + 1 },
      { x: top.x - 7, y: top.y - 7 },
    ],
    '#5bc8f0',
  );
  pc.px(top.x - 2, top.y - 11, '#ffffff');
  pc.rect(top.x - 9, top.y + 2, 2, 8, '#bfe8ff');
  pc.rect(top.x + 8, top.y + 2, 2, 8, '#bfe8ff');
}

function drawCrystalPillar(pc: PixelCanvas): void {
  drawBox(pc, box(-0.4, 0.4, -0.4, 0.4, 0, 6, faces('#6a6f7a', '#8a909c')));
  drawBox(pc, box(-0.24, 0.24, -0.24, 0.24, 6, 70, faces('#3f9fd0', '#8fd8f5')));
  drawBox(pc, box(-0.16, 0.16, -0.16, 0.16, 70, 84, faces('#5bc8f0', '#d8f4ff')));
  const glint = iso(-0.1, 0.24, 40);
  pc.rect(glint.x, glint.y - 16, 2, 20, '#eafaff');
}

const drawDiamondThrone = throneDrawer(
  { frame: ['#3f9fd0', '#8fd8f5'], plinth: ['#6a6f7a', '#a9b0b7'], cushion: ['#5bc8f0', '#d8f4ff'], gem: '#ffffff' },
  'diamond',
);

/** The Phoenix Throne: a limited Kaki Dollar drop (see furniture/limited.ts). */
const drawPhoenixThrone = throneDrawer(
  { frame: ['#8a2d10', '#d9601f'], plinth: ['#4a4650', '#6d6875'], cushion: ['#ff8a1f', '#ffe27a'], gem: '#ffd23a' },
  'diamond',
);

/**
 * A dragon lamp in one scale colour. The flame in its mouth is not painted
 * here: the client animates it from the item's `effect`.
 */
function dragonDrawer(scale: string, belly: string): FurniDrawer {
  return (pc) => {
    // Stone plinth.
    drawBox(pc, box(-0.38, 0.38, -0.38, 0.38, 0, 8, faces('#4a4650', '#6d6875')));
    // Coiled body, then the neck rising out of it.
    drawBox(pc, box(-0.32, 0.32, -0.28, 0.28, 8, 24, faces(scale)));
    drawBox(pc, box(-0.3, 0.1, -0.24, 0.24, 24, 32, faces(shade(scale, 0.9))));
    drawBox(pc, box(-0.14, 0.14, -0.14, 0.14, 32, 50, faces(scale)));
    // Belly plates down the front of the neck.
    const front = iso(0.14, 0.14, 0);
    for (let h = 34; h < 50; h += 4) pc.rect(front.x - 3, front.y - h, 6, 2, belly);
    // Head with a snout, horns and an eye.
    drawBox(pc, box(-0.12, 0.26, -0.16, 0.16, 50, 60, faces(scale)));
    drawBox(pc, box(0.14, 0.34, -0.1, 0.1, 50, 56, faces(shade(scale, 1.1))));
    const head = iso(0, 0, 60);
    pc.rect(head.x - 5, head.y - 6, 2, 6, belly);
    pc.rect(head.x + 3, head.y - 6, 2, 6, belly);
    const eye = iso(0.1, 0.16, 56);
    pc.rect(eye.x, eye.y, 2, 2, '#fff2a0');
    pc.px(eye.x + 1, eye.y + 1, '#1a1216');
    // Back spikes down the coil.
    for (let i = 0; i < 4; i++) {
      const spike = iso(-0.28 + i * 0.14, -0.2, 24);
      pc.rect(spike.x, spike.y - 5, 2, 5, belly);
    }
  };
}

/**
 * A rare toggleable door (see `packages/protocol` furniture/rare.ts): steel
 * posts and a lintel always stand; three glowing beams strung between them
 * when closed, gone once it's powered down (open) so people can walk through.
 */
const drawLaserDoor: FurniDrawer = (pc, rotation, open) => {
  const height = 66;
  const along = rotation === 0 || rotation === 4;
  const frame = faces('#7d848c', '#c7ccd1');
  const post = (side: -1 | 1) =>
    along
      ? box(0.3 * side - 0.12, 0.3 * side + 0.12, -0.09, 0.09, 0, height, frame)
      : box(-0.09, 0.09, 0.3 * side - 0.12, 0.3 * side + 0.12, 0, height, frame);
  const lintel = along
    ? box(-0.42, 0.42, -0.09, 0.09, height - 8, height, frame)
    : box(-0.09, 0.09, -0.42, 0.42, height - 8, height, frame);
  drawBoxes(pc, [post(-1), post(1), lintel]);
  if (open) return;

  for (let i = 0; i < 3; i += 1) {
    const h = 10 + i * 16;
    const a = along ? iso(-0.18, 0, h) : iso(0, -0.18, h);
    const b = along ? iso(0.18, 0, h) : iso(0, 0.18, h);
    pc.lineF(a.x, a.y, b.x, b.y, '#ff3b3b', 1.4);
    pc.lineF(a.x, a.y, b.x, b.y, '#ffb0b0', 0.6);
  }
};

export const RARE_DRAWERS: Record<string, FurniDrawer> = {
  golden_cat: drawGoldenCat,
  gold_throne: drawGoldThrone,
  diamond_fountain: drawDiamondFountain,
  crystal_pillar: drawCrystalPillar,
  diamond_throne: drawDiamondThrone,
  phoenix_throne: drawPhoenixThrone,
  door_laser: drawLaserDoor,
  dragon_red: dragonDrawer('#b8312a', '#f0b04a'),
  dragon_blue: dragonDrawer('#2c62b8', '#9fe0ff'),
  dragon_green: dragonDrawer('#2f8f4a', '#d8f08a'),
  dragon_purple: dragonDrawer('#7a3fc0', '#e8c8ff'),
  dragon_black: dragonDrawer('#2a2830', '#7a5cff'),
};
